import { ConfigProvider } from 'antd'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'

import { Confirmation } from '@components/Confirmation'
import { Notifications } from '@components/Notifications'
import { initializeSentry } from '@connectors/SentryClient'
import { AuthProvider, ProtectedRoute } from '@hooks/useAuth'
import { AuthenticatedLayout } from '@layouts/AuthenticatedLayout'
import { UnauthenticatedLayout } from '@layouts/UnauthenticatedLayout'
import * as Sentry from '@sentry/react'

import BatchDashboardPage from './admin-batch/pages/DashboardPage/DashboardPage'
import { CouponsDashboardPage } from './admin-coupons/pages/CouponsDashboardPage/CouponsDashboardPage'
import { BundlesPage } from './admin-curriculum/pages/Bundles/BundlesPage'
import { CurriculumListPage } from './admin-curriculum/pages/Curriculum/CurriculumListPage'
import { BuilderPage } from './admin-curriculum/pages/CurriculumBuilder/BuilderPage'
import { YearLevelListPage } from './admin-curriculum/pages/YearLevel/YearLevelListPage'
import { EmailMessagesDashboardPage } from './admin-emails/pages/DashboardPage/DashboardPage'
import { FeatureFlagDashboardPage } from './admin-feature-flags/FeatureFlagDashboardPage'
import { DashboardPage } from './admin-general/pages/DashboardPage'
import { ErrorPage } from './admin-general/pages/Error'
import { LoginPage } from './admin-general/pages/Login'
import { LogoutPage } from './admin-general/pages/Logout'
import { MFAPage } from './admin-general/pages/MFAPage/MFAPage'
import { InvitesDashboardPage } from './admin-invites/pages/InvitesDashboardPage/InvitesDashboardPage'
import { ProductDashboardPage } from './admin-products/pages/ProductDashboardPage/ProductDashboardPage'
import { ProductDetailsPage } from './admin-products/pages/ProductDetailsPage/ProductDetailsPage'
import { PublisherDashboardPage } from './admin-publisher/pages/PublisherDashboardPage/PublisherDashboardPage'
import { PublisherDetailsPage } from './admin-publisher/pages/PublisherDetailsPage/PublisherDetailsPage'
import { ReportDashboardPage } from './admin-reports/pages/ReportDashboardPage/ReportDashboardPage'
import { SchoolDashboardPage } from './admin-schools/pages/SchoolDashboardPage/SchoolDashboardPage'
import { SchoolDetailsPage } from './admin-schools/pages/SchoolDetailsPage/SchoolDetailsPage'
import { SettingsDashboardPage } from './admin-settings/pages/SettingsDashboardPage'
import SubscriptionDashboardPage from './admin-subscriptions/pages/DashboardPage/DashboardPage'
import { UserDashboardPage } from './admin-users/pages/UserDashboardPage/UserDashboardPage'
import { UserDetailsPage } from './admin-users/pages/UserDetailsPage/UserDetailsPage'

import 'quill/dist/quill.bubble.css'
import 'quill/dist/quill.snow.css'

if (import.meta.env.VITE_NODE_ENV === 'production') {
    initializeSentry()
}

const router = createBrowserRouter(
    [
        {
            path: '/login',
            element: (
                <UnauthenticatedLayout>
                    <LoginPage />
                </UnauthenticatedLayout>
            ),
            errorElement: <ErrorPage />
        },
        {
            path: '/logout',
            element: <LogoutPage />
        },
        {
            path: '/',
            element: (
                <ProtectedRoute>
                    <AuthenticatedLayout />
                </ProtectedRoute>
            ),
            errorElement: <ErrorPage />,
            children: [
                {
                    path: '/',
                    element: <DashboardPage />
                },
                {
                    path: '/users',
                    element: <UserDashboardPage />
                },
                {
                    path: '/users/:userId',
                    element: <UserDetailsPage />
                },
                {
                    path: '/schools',
                    element: <SchoolDashboardPage />
                },
                {
                    path: '/schools/:id',
                    element: <SchoolDetailsPage />
                },
                {
                    path: '/subscriptions',
                    element: <SubscriptionDashboardPage />
                },
                {
                    path: '/products',
                    element: <ProductDashboardPage />
                },
                {
                    path: '/products/:id',
                    element: <ProductDetailsPage />
                },
                {
                    path: '/coupons',
                    element: <CouponsDashboardPage />
                },
                {
                    path: '/reports',
                    element: <ReportDashboardPage />
                },
                {
                    path: '/schools',
                    element: <SchoolDashboardPage />
                },
                {
                    path: '/curriculums',
                    element: <CurriculumListPage />
                },
                {
                    path: '/year-levels',
                    element: <YearLevelListPage />
                },
                {
                    path: '/curriculum-builder/:curriculumId/:objectiveCategoryId',
                    element: <BuilderPage />
                },
                {
                    path: '/curriculum-builder/:curriculumId',
                    element: <BuilderPage />
                },
                {
                    path: '/curriculum-builder',
                    element: <BuilderPage />
                },
                {
                    path: '/bundles',
                    element: <BundlesPage />
                },
                {
                    path: '/publishers',
                    element: <PublisherDashboardPage />
                },
                {
                    path: '/publishers/:id',
                    element: <PublisherDetailsPage />
                },
                {
                    path: '/invitees',
                    element: <InvitesDashboardPage />
                },
                {
                    path: '/email-messages',
                    element: <EmailMessagesDashboardPage />
                },
                {
                    path: '/batch',
                    element: <BatchDashboardPage />
                },
                {
                    path: '/feature-flags',
                    element: <FeatureFlagDashboardPage />
                },
                {
                    path: '/settings',
                    element: <SettingsDashboardPage />
                },
                {
                    path: '/mfa',
                    element: <MFAPage />
                }
            ]
        },
        {
            path: '/logout',
            element: (
                <UnauthenticatedLayout>
                    <LogoutPage />
                </UnauthenticatedLayout>
            ),
            errorElement: <ErrorPage />
        }
    ],
    {
        basename: '/'
    }
)

const Main = () => {
    return (
        <>
            <Notifications />
            <Confirmation />
            <RouterProvider router={router} />
        </>
    )
}

// Important: since strict mode is enabled in dev env, it double the renders.
// For some reason pixi board is not destroyed properly on second rerndering..
// I disabled strict mode until we figure this out..

// As well: In development mode, React will rethrow errors caught within an error boundary.
// This will result in errors being reported twice to Sentry with the above setup, but this won't occur in the production build.
ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
        <AuthProvider>
            <ConfigProvider
                theme={{
                    token: {}
                }}
            >
                <Main />
            </ConfigProvider>
        </AuthProvider>
    </Sentry.ErrorBoundary>
)
